'use client'

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import TableWrapper from '@/components/common/table/TableWrapper'
import AvatarCell from '@/components/common/AvatarCell'
import IndeterminateCheckbox from '@/components/common/IndeterminateCheckbox'
import StatusPill from '@/components/common/StatusPill'
import {
  deleteEmployee,
  deleteEmployeeList,
  getEmployees,
  initialEmployee,
  prepareForExport,
} from '@/lib/employee/Employee'
import { Employee, ExportFilterEmployee } from '@/lib/employee/Employee.types'
import { className2 } from '@/components/common/styles'
import { performExport } from '@/components/common/table/performExport'
import type {
  TableWrapperCellContext,
  TableWrapperColumnDef,
} from '@/lib/table/table'

const actionsFunction = ({ row }: { row: { original: Employee } }) => (
  <div>
    <button
      type="button"
      className={className2}
      onClick={() => {
        console.log('clicked button for row=', row)
        performExport(prepareForExport([row.original]))
      }}
    >
      Download
    </button>
    <button
      type="button"
      className={className2}
      onClick={() => {
        console.log('row.original.id=', row.original.id)
        const result = deleteEmployee(row.original.id)
        console.log('result=', result)
      }}
    >
      Delete
    </button>
  </div>
)

const ageFunction = (info: TableWrapperCellContext<Employee>) => {
  return <div className="mr-6">{info.getValue<number>()}</div>
}

const titleFunction = (info: TableWrapperCellContext<Employee>) => {
  return <i>{info.getValue<string | null>() ?? ''}</i>
}

const EmployeesTable = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['getEmployees'],
    queryFn: () => getEmployees(),
    placeholderData: [initialEmployee],
  })
  // @@TODO: console.log('data=', data)
  const [exportData, setExportData] = useState([] as ExportFilterEmployee[])

  const listItems = data
    ? data.map(({ id, name, title, status, age, role, imgUrl }) => (
        <li key={id}>
          id={id} - name={name} title={title} status={status} age={age} role=
          {role} imgUrl={imgUrl}
        </li>
      ))
    : null

  const columns = useMemo<Array<TableWrapperColumnDef<Employee>>>(
    () => [
      {
        accessorKey: 'select',
        id: 'select',
        enableSorting: false,
        header: ({ table }) => (
          <div className="px-5">
            <IndeterminateCheckbox
              {...{
                'aria-label': 'select all',
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                'aria-label': 'select',
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        accessorKey: 'id',
        id: 'id',
        cell: (info) => <div className="mr-6">{info.getValue<number>()}</div>,
      },
      {
        accessorKey: 'name',
        id: 'name',
        cell: (props) => (
          <div>
            <AvatarCell
              imgUrl={props.row.original['imgUrl'] ?? ''}
              name={props.row.original['name'] ?? ''}
            />
          </div>
        ),
        filterFn: 'fuzzy',
      },
      {
        accessorKey: 'title',
        id: 'title',
        cell: (info) => titleFunction(info),
      },
      {
        accessorKey: 'status',
        id: 'status',
        cell: (info) => StatusPill({ value: info.renderValue() as string }),
      },
      {
        accessorKey: 'age',
        id: 'age',
        cell: (info) => ageFunction(info),
      },
      {
        accessorKey: 'role',
        id: 'role',
      },
      {
        accessorKey: 'action',
        id: 'action',
        enableSorting: false,
        cell: (row) => actionsFunction(row),
      },
    ],
    [],
  )

  return (
    <div>
      <TableWrapper<Employee>
        data={data as Employee[]}
        columns={columns}
        exportData={exportData}
        deleteWithList={deleteEmployeeList}
        prepareForExport={prepareForExport}
        name="employees"
      />
    </div>
  )
}

export default EmployeesTable
